import type {
  FieldFunctionOptions,
  FieldPolicy,
  FieldReadFunction,
  Reference,
  StoreObject,
} from '@apollo/client';
import type {
  Analytics,
  AnnotationMap,
  Channel,
  Dataset,
  DatasetJob,
  Editor,
  GANModel,
  Graph,
  MLInference,
  MLModel,
  Member,
  Service,
  UMAP,
  Volume,
  VolumeData,
  Workspace,
} from '@generated/graphql';

function createStandardMergeFunction<T extends Reference | StoreObject>(idField: string) {
  return (
    existing: Record<string, T>,
    incoming: T[],
    { readField, variables }: FieldFunctionOptions<T>,
  ) => {
    // If no cursor/pagination, replace entirely (fresh fetch)
    if (!variables?.cursor && !variables?.offset) {
      const merged = {};
      incoming?.forEach((item) => {
        merged[String(readField(idField, item))] = item;
      });
      return merged;
    }
    // Otherwise, merge for pagination
    const merged = { ...existing };
    incoming?.forEach((item) => {
      merged[String(readField(idField, item))] = item;
    });
    return merged;
  };
}

const standardRead: FieldReadFunction = <T>(existing: Record<string, T>) => {
  return existing && Object.values(existing);
};

const createStandardPolicy = <T extends Reference | StoreObject>(
  idField: string,
  keyArgs?: string[],
) => {
  const policy: FieldPolicy<Record<string, T>, T[]> = {};
  policy.keyArgs = keyArgs ?? [idField];
  policy.merge = createStandardMergeFunction<T>(idField);
  policy.read = standardRead;
  return policy;
};

export const QueryPolicies = {
  getAnalytics: createStandardPolicy<Analytics>('analyticsId', [
    'workspaceId',
    'analyticsId',
    'datasetId',
    'filters',
  ]),
  getAnnotationMaps: createStandardPolicy<AnnotationMap>('mapId', [
    'organizationId',
    'workspaceId',
    'mapId',
    'filters',
  ]),
  getChannels: createStandardPolicy<Channel>('channelId', [
    'organizationId',
    'workspaceId',
    'channelId',
    'filters',
  ]),
  getDatasetFiles: {
    keyArgs: ['workspaceId', 'datasetId', 'path'],
    merge(existing, incoming) {
      if (!existing) {
        return incoming;
      }
      return [...existing, ...incoming];
    },
  } as FieldPolicy<string[], string[]>,
  getDatasetJobs: createStandardPolicy<DatasetJob>('datasetId', [
    'organizationId',
    'workspaceId',
    'filters',
  ]),
  getDatasets: createStandardPolicy<Dataset>('datasetId', ['workspaceId', 'datasetId', 'filters']),
  getEditors: createStandardPolicy<Editor>('editorId', [
    'organizationId',
    'workspaceId',
    'editorId',
    'filters',
    'sort',
  ]),
  getGANModels: createStandardPolicy<GANModel>('modelId', [
    'organizationId',
    'workspaceId',
    'modelId',
    'filters',
  ]),
  getGraphs: createStandardPolicy<Graph>('graphId', [
    'workspaceId',
    'graphId',
    'staged',
    'filters',
  ]),
  getMembers: createStandardPolicy<Member>('userId', ['organizationId', 'limit']),
  getMLModels: createStandardPolicy<MLModel>('modelId', [
    'workspaceId',
    'datasetId',
    'modelId',
    'filters',
  ]),
  getMLInferences: createStandardPolicy<MLInference>('inferenceId', [
    'workspaceId',
    'modelId',
    'datasetId',
    'inferenceId',
    'filters',
  ]),
  getServices: createStandardPolicy<Service>('serviceId', [
    'organizationId',
    'workspaceId',
    'serviceId',
    'filters',
  ]),
  getUMAPs: createStandardPolicy<UMAP>('umapId', ['workspaceId', 'datasetId', 'umapId', 'filters']),
  getVolumeData: createStandardPolicy<VolumeData>('key', [
    'volumeId',
    'dir',
    'keys',
    'filters',
    'sort',
    'recursive',
  ]),
  getVolumes: createStandardPolicy<Volume>('volumeId', [
    'organizationId',
    'workspaceId',
    'serviceId',
    'volumeId',
    'filters',
  ]),
  getWorkspaces: createStandardPolicy<Workspace>('workspaceId', [
    'organizationId',
    'workspaceId',
    'filters',
  ]),
};
